import { createAsyncThunk } from '@reduxjs/toolkit';
import adserver from 'services/adserver';
import { withErrorHandling } from 'store/wrappers';
import { REDUCER_KEY } from './committedProposals.consts';
import { ProposalParams, ProposalListParams } from '../proposals.types';
import { selectCurrentCampaignId } from 'store/campaigns/campaigns.selectors';
import { fetchAllCommittedFlightsOfProposal } from 'store/flights/committed';

import { normalizeDateFromApi } from 'helpers/datetimeHelpers';

function normalizeApiReponse(data: ProposalParams) {
  if (!data.productSnapshot.timedSegments) {
    return data;
  }
  const segments = data.productSnapshot.timedSegments.map(segment => ({
    ...segment,
    startDate: normalizeDateFromApi(segment.startDate),
    endDate: segment.endDate ? normalizeDateFromApi(segment.endDate) : null,
  }));
  return {
    ...data,
    productSnapshot: { ...data.productSnapshot, timedSegments: segments },
  };
}

const fetchByIdHandler = async ({ proposalId }: { proposalId: number }) => {
  const proposal = await adserver({
    url: `/proposal/${proposalId}/committed`,
    headers: {},
  });

  const surcharges = await adserver({
    url: `/proposal/${proposalId}/surcharges`,
    headers: {},
  });

  return { ...proposal.data, surcharges: surcharges?.data };
};

export const fetchAllCommittedProposals = createAsyncThunk(
  REDUCER_KEY + '/fetchAll',
  withErrorHandling(async () => {
    const response = await adserver({ url: '/proposals', headers: {} });
    return response.data.map((proposal: ProposalListParams) => proposal);
  }),
);

export const fetchById = createAsyncThunk(
  REDUCER_KEY + '/getCommittedProposal',
  withErrorHandling(async ({ proposalId }: { proposalId: number }) => {
    const response = await adserver({
      url: `/proposal/${proposalId}/committed`,
      headers: {},
    });
    const surcharges = await adserver({
      url: `/proposal/${proposalId}/surcharges`,
      headers: {},
    });

    return {
      ...response.data,
      surcharges: surcharges?.data,
    };
  }),
);

export const setCurrentCommittedProposal = createAsyncThunk(
  REDUCER_KEY + '/current',
  withErrorHandling(async (proposalId: number, thunkAPI: any) => {
    if (!proposalId) return;
    const proposal = await fetchByIdHandler({ proposalId });
    thunkAPI.dispatch(fetchAllCommittedFlightsOfProposal({ proposalId }));
    return proposal;
  }),
);

export const fetchCommittedProposalsOfCampaign = createAsyncThunk(
  REDUCER_KEY + '/fetchCommittedProposalsOfCampaign',
  withErrorHandling(async ({ campaignId }: { campaignId: number }) => {
    const response = await adserver({
      url: `/campaign/${campaignId}/committed/proposals`,
      headers: {},
    });
    return response.data.map((deal: ProposalParams) => deal);
  }),
);

export const fetchCommittedProposalsOfBasket = createAsyncThunk(
  REDUCER_KEY + '/fetchCommittedProposalsOfBasket',
  withErrorHandling(async ({ basketId }: { basketId: number }) => {
    const response = await adserver({
      url: `/basket/${basketId}/committed/proposals`,
      headers: {},
    });

    return response.data.map((deal: ProposalParams) => ({
      ...normalizeApiReponse(deal),
      basket: basketId,
    }));
  }),
);

export const fetchAllCommittedProposalsForCurrentCampaign = createAsyncThunk(
  REDUCER_KEY + '/fetchAllCommittedProposalsForCurrentCampaign',
  withErrorHandling(async (_: any, thunkApi: any) => {
    const campaignId = selectCurrentCampaignId(thunkApi.getState());
    const response = await adserver({
      url: `/campaign/${campaignId}/committed/proposals`,
      headers: {},
    });

    return response.data.map((deal: ProposalParams) => deal);
  }),
);

export const removeAllCommittedProposals = (): { type: string } => ({
  type: REDUCER_KEY + '/removeAll',
});

export const fetchProposalFormRate = createAsyncThunk(
  REDUCER_KEY + '/fetchFormRate',
  withErrorHandling(
    async ({
      proposalId,
      formData,
    }: {
      proposalId: number;
      formData: ProposalParams;
    }) => {
      const response = await adserver({
        url: `/proposal/${proposalId}/rate`,
        method: 'POST',
        data: formData,
        timeout: 10000,
        headers: {},
      });
      return response;
    },
  ),
);
